import { Button } from "../../components/Button";
import { ALL_ACCESS_MINOR, naira } from "../../lib/format";
import { ParentSurface } from "./ParentSurface";
import { PaymentBusy, type PaymentStep } from "../../components/PaymentBusy";

interface ParentPayScreenProps {
  childName: string;
  daysPractisedOfLastSeven: number;
  onPay?: (() => void) | undefined;
  /** Opening Paystack, or confirming a charge it reported. */
  step?: PaymentStep | undefined;
}

const INCLUDED = [
  "Every subject your child chose, unlocked",
  "Unlimited past questions & topic practice",
  "AI tutor and weekly mock exams",
  "Progress reports sent to this number",
];

/**
 * Screen 22b — the parent's payment page. No login, opaque reference in the URL.
 *
 * It leads with what the child has already done. A parent asked for ₦5,000 by a
 * teenager wants evidence the app is being used, and "4 of the last 7 days" is
 * the only honest answer to that.
 */
export function ParentPayScreen({
  childName,
  daysPractisedOfLastSeven,
  onPay,
  step = null,
}: ParentPayScreenProps) {
  return (
    <ParentSurface>
      <h1 className="m-0 pt-8 font-[family-name:var(--font-display)] text-[22px] font-bold leading-[1.25]">
        {childName} asked you to unlock full practice.
      </h1>
      <p className="tabular m-0 pt-3 text-[14px] leading-[1.6] text-text-muted">
        {childName} has been practicing {daysPractisedOfLastSeven} of the last 7 days. One payment
        covers everything until the exam — no renewals, no surprises.
      </p>

      <div className="mt-6 rounded-[var(--radius-card)] border border-border bg-surface p-5">
        <p className="tabular m-0 font-[family-name:var(--font-display)] text-[34px] font-bold leading-none">
          {naira(ALL_ACCESS_MINOR)}
        </p>
        <p className="m-0 pt-1 text-[13px] text-text-muted">once, till the exam</p>

        <ul className="m-0 flex list-none flex-col gap-2.5 p-0 pt-4">
          {INCLUDED.map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-[13.5px] leading-[1.45]">
              <span aria-hidden="true" className="pt-0.5 text-positive">
                ✓
              </span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-auto flex flex-col gap-3 pt-8">
        <Button disabled={step !== null} onClick={onPay}>
          {step === "opening" ? "Opening secure checkout…" : "Pay with Paystack"}
        </Button>
        <p className="m-0 text-center text-[12px] leading-[1.5] text-text-muted">
          Secured by Paystack · Card, transfer or USSD · Receipt by SMS
        </p>
      </div>
      <PaymentBusy step={step} />
    </ParentSurface>
  );
}
